/**
 * PatientSelectModal — Centered modal for patient search & quick-add.
 *
 * Reuses the usePatientQuickAdd hook (search + create) and renders
 * the same UI as PatientQuickCard but inside a proper modal dialog.
 *
 * Shown when navigating to /patient without patient context.
 * On select/add → navigates to /patient with patientId.
 * On cancel → navigate(-1) back to previous page.
 */
import React, { useRef, useEffect, useState, useCallback } from "react";
import { Search, UserPlus, ArrowLeftRight, X, Save } from "lucide-react";
import { usePatientQuickAdd } from "../hooks/usePatientQuickAdd";
import ModalPortal from "../utils/ModalPortal";

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen - Whether the modal is visible
 * @param {Function} props.onPatientSelect - Called with patient object when selected/created
 * @param {Function} props.onCancel - Called when user dismisses the modal
 */
const PatientSelectModal = ({ isOpen, onPatientSelect, onCancel }) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    filteredPatients,
    isLoading,
    searchQuery,
    setSearchQuery,
    showAddForm,
    setShowAddForm,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    isCreating,
    canShowAddNew,
    canCreate,
    hasTwoWords,
    handleAddNewClick,
    handleSwapNames,
    selectPatient,
    createPatient,
    quickCreatePatient,
    reset,
  } = usePatientQuickAdd({
    enabled: isOpen,
    onPatientSelect: (patient) => {
      reset();
      onPatientSelect?.(patient);
    },
  });

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Open dropdown when typing
  const handleSearchChange = useCallback(
    (e) => {
      setSearchQuery(e.target.value);
      setShowAddForm(false);
      setIsDropdownOpen(true);
    },
    [setSearchQuery, setShowAddForm]
  );

  // Handle patient selection from dropdown
  const handleSelectPatient = useCallback(
    (patient) => {
      setIsDropdownOpen(false);
      selectPatient(patient);
    },
    [selectPatient]
  );

  // Handle "Add new" click
  // Two words → create immediately; one word → show name confirmation form
  const handleAddNew = useCallback(async () => {
    setIsDropdownOpen(false);
    if (hasTwoWords) {
      await quickCreatePatient();
    } else {
      handleAddNewClick();
    }
  }, [hasTwoWords, quickCreatePatient, handleAddNewClick]);

  // Handle create patient
  const handleCreate = useCallback(async () => {
    await createPatient();
  }, [createPatient]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    reset();
    onCancel?.();
  }, [reset, onCancel]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (showAddForm) {
          setShowAddForm(false);
        } else if (isDropdownOpen) {
          setIsDropdownOpen(false);
        } else {
          handleCancel();
        }
      } else if (e.key === "Enter" && showAddForm && canCreate) {
        handleCreate();
      }
    },
    [showAddForm, isDropdownOpen, canCreate, handleCreate, handleCancel, setShowAddForm]
  );

  // Cap dropdown results so the list stays a quick-pick (not a full directory).
  // The dropdown container scrolls (max-height in Modal.css), so users can scroll
  // through the visible matches; if more exist beyond the cap they keep typing
  // to narrow. See docs/updates/2026-05-01-cannot-scroll-other-patients.md.
  const RESULTS_CAP = 50;
  const displayPatients = filteredPatients.slice(0, RESULTS_CAP);
  const hasMoreResults = filteredPatients.length > RESULTS_CAP;

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="modal" onClick={handleCancel}>
        <div
          className="modal-content patient-select-modal"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          {/* Header */}
          <div className="patient-select-modal-header">
            <div className="patient-select-modal-title-row">
              <UserPlus size={20} className="patient-select-modal-icon" />
              <h2 className="modal-title" style={{ margin: 0 }}>
                {showAddForm ? "Add New Patient" : "Select Patient"}
              </h2>
            </div>
            <button
              className="patient-select-modal-close"
              onClick={handleCancel}
              aria-label="Cancel"
            >
              <X size={18} />
            </button>
          </div>

          <p className="patient-select-modal-subtitle">
            {showAddForm
              ? "Enter the patient's name to create a new record."
              : "Search for an existing patient or add a new one."}
          </p>

          {/* Search mode */}
          {!showAddForm && (
            <div className="patient-select-modal-search-container">
              <div className="patient-select-modal-search-wrapper">
                <Search size={16} className="patient-select-modal-search-icon" />
                <input
                  ref={inputRef}
                  type="text"
                  className="patient-select-modal-search-input userback-ignore"
                  placeholder="Search by patient name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => setIsDropdownOpen(true)}
                  autoComplete="off"
                />
              </div>

              {/* Dropdown results */}
              {isDropdownOpen && (searchQuery.trim() || filteredPatients.length > 0) && (
                <div ref={dropdownRef} className="patient-select-modal-dropdown userback-ignore">
                  {isLoading ? (
                    <div className="patient-select-modal-dropdown-status">Loading...</div>
                  ) : (
                    <>
                      {displayPatients.map((patient) => (
                        <button
                          key={patient.id}
                          type="button"
                          className="patient-select-modal-dropdown-item"
                          onClick={() => handleSelectPatient(patient)}
                        >
                          <span className="patient-select-modal-dropdown-name">
                            {patient.lastName}
                            {patient.lastName && patient.firstName ? ", " : ""}
                            {patient.firstName}
                          </span>
                          {patient.dateOfBirth && (
                            <span className="patient-select-modal-dropdown-meta">
                              {patient.dateOfBirth}
                            </span>
                          )}
                        </button>
                      ))}
                      {hasMoreResults && (
                        <div className="patient-select-modal-dropdown-status">
                          Showing first {RESULTS_CAP} — keep typing to narrow.
                        </div>
                      )}
                      {displayPatients.length === 0 && searchQuery.trim() && (
                        <div className="patient-select-modal-dropdown-status">
                          No matches for &ldquo;{searchQuery}&rdquo;
                        </div>
                      )}
                      {canShowAddNew && (
                        <button
                          type="button"
                          className="patient-select-modal-dropdown-add"
                          onClick={handleAddNew}
                          disabled={isCreating}
                        >
                          <UserPlus size={14} />
                          {isCreating ? "Adding..." : `Add \u201c${searchQuery.trim()}\u201d as new patient`}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Add mode */}
          {showAddForm && (
            <div className="patient-select-modal-add-form">
              <div className="patient-select-modal-name-row">
                <div className="patient-select-modal-field">
                  <label className="patient-select-modal-label">First Name</label>
                  <input
                    ref={inputRef}
                    type="text"
                    className="patient-select-modal-name-input"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <button
                  type="button"
                  className="patient-select-modal-swap-btn"
                  onClick={handleSwapNames}
                  title="Swap names"
                >
                  <ArrowLeftRight size={16} />
                </button>
                <div className="patient-select-modal-field">
                  <label className="patient-select-modal-label">Last Name</label>
                  <input
                    type="text"
                    className="patient-select-modal-name-input"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="modal-button-container">
                <button
                  type="button"
                  className="button-cancel-secondary"
                  onClick={() => setShowAddForm(false)}
                >
                  <X size={15} /> Back to Search
                </button>
                <button
                  type="button"
                  className="button-confirm"
                  onClick={handleCreate}
                  disabled={!canCreate || isCreating}
                >
                  <Save size={15} /> {isCreating ? "Adding..." : "Add Patient"}
                </button>
              </div>
            </div>
          )}

          {/* Footer cancel */}
          {!showAddForm && (
            <div className="patient-select-modal-footer">
              <button
                type="button"
                className="button-cancel-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default PatientSelectModal;
